import React, { useState } from 'react';
import { Download, Users, UserX, Edit2, Trash2, UserPlus } from 'lucide-react';

const ContactGroupList = ({
  contactGroups,
  loading,
  onDownloadGroup,
  onViewGroupDetails,
  onEditGroup,
  onDeleteGroup,
  onAddContacts,
  selectedGroup
}) => {
  const [hoveredGroup, setHoveredGroup] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-sm text-gray-600">Loading...</span>
      </div>
    );
  }

  if (contactGroups.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-3">
          <Users className="mx-auto h-10 w-10" />
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-1">No contact groups found</h3>
        <p className="text-xs text-gray-500">Upload an Excel file to create groups.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {contactGroups.map((group) => {
        const isSelected = selectedGroup?.groupId === group.groupId;
        
        return (
          <div
            key={group.groupId}
            onMouseEnter={() => setHoveredGroup(group.groupId)}
            onMouseLeave={() => setHoveredGroup(null)}
            onClick={() => onViewGroupDetails && onViewGroupDetails(group)}
            className={`px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer group ${
              isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : ''
            }`}
          >
          <div className="flex items-center gap-3">
            {/* Group Icon - Smaller */}
            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="h-5 w-5 text-primary-600" />
            </div>
            
            {/* Group Details - Compact */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {group.groupName}
                </h3>
                
                {/* Actions - Show on hover */}
                {hoveredGroup === group.groupId && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {onAddContacts && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddContacts(group);
                        }}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Add contacts"
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownloadGroup && onDownloadGroup(group);
                      }}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {onEditGroup && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGroup(group);
                        }}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                    {onDeleteGroup && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteGroup(group);
                        }}
                        className="p-1.5 text-gray-500 hover:text-error-600 hover:bg-error-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Statistics - Compact inline */}
              <div className="flex items-center gap-3 mt-1">
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <Users className="h-3 w-3 text-green-600" />
                  <span>{group.totalContacts}</span>
                </div>
                {group.unsubscribedCount > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <UserX className="h-3 w-3 text-red-600" />
                    <span>{group.unsubscribedCount}</span>
                  </div>
                )}
                {group.lastUpdated && (
                  <span className="text-xs text-gray-400">
                    {new Date(group.lastUpdated).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default ContactGroupList;
